import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, Users, TrendingUp, Briefcase, Activity,
  ArrowRight, BarChart3, PieChart, LogOut, ArrowRightLeft, UserCog, Mail
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OwnerDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, switchAMC } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAmcSwitcher, setShowAmcSwitcher] = useState(false);
  const [switching, setSwitching] = useState(false);
  const amcMemberships = user?.amc_memberships || [];
  const canManageTeam = (user?.owner_capabilities?.is_super) || (user?.owner_capabilities?.can_manage_owner_team);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API_URL}/owner/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const StatCard = ({ icon: Icon, title, value, subtitle, color }) => (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg ${color} flex items-center justify-center`}>
          <Icon size={24} className="text-white" />
        </div>
      </div>
      <h3 className="text-gray-600 text-sm font-medium mb-1">{title}</h3>
      <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
      {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Building2 size={32} className="text-blue-600 mr-3" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">PrimeAIF Owner Portal</h1>
                <p className="text-xs text-gray-500">Platform Administration</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {amcMemberships.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setShowAmcSwitcher(!showAmcSwitcher)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                    data-testid="owner-amc-switcher-btn"
                  >
                    <ArrowRightLeft size={16} />
                    Switch to AMC
                  </button>
                  {showAmcSwitcher && (
                    <div
                      className="absolute right-0 top-full mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden"
                      data-testid="owner-amc-switcher-dropdown"
                    >
                      <div className="px-3 py-2 bg-gray-50 border-b">
                        <span className="text-xs font-medium text-gray-500">Your AMC workspaces</span>
                      </div>
                      {amcMemberships.map((m) => (
                        <button
                          key={m.amc_id}
                          onClick={async () => {
                            try {
                              setSwitching(true);
                              await switchAMC(m.amc_id);
                              setShowAmcSwitcher(false);
                              navigate('/funds');
                              window.location.reload();
                            } catch (err) {
                              alert(err.response?.data?.detail || 'Failed to switch');
                            } finally {
                              setSwitching(false);
                            }
                          }}
                          disabled={switching}
                          className="w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50 border-b last:border-b-0 flex items-center gap-2 text-gray-700"
                          data-testid={`owner-switch-to-${m.amc_id}`}
                        >
                          <Building2 size={14} className="text-blue-500" />
                          <div className="flex-1">
                            <div className="font-medium">{m.amc_name}</div>
                            <div className="text-xs text-gray-500 capitalize">{m.role}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-blue-600">Platform Owner</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut size={18} className="mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome back, {user?.name?.split(' ')[0]}!</h2>
          <p className="text-gray-600">Here's what's happening with your platform today.</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={Building2}
            title="Total AMCs"
            value={analytics?.total_amcs || 0}
            subtitle={`${analytics?.active_amcs || 0} active`}
            color="bg-blue-600"
          />
          <StatCard
            icon={Users}
            title="Total Users"
            value={analytics?.total_users || 0}
            subtitle="Across all AMCs"
            color="bg-green-600"
          />
          <StatCard
            icon={Briefcase}
            title="Total Funds"
            value={analytics?.total_funds || 0}
            subtitle="Managed by AMCs"
            color="bg-purple-600"
          />
          <StatCard
            icon={TrendingUp}
            title="Total Investments"
            value={analytics?.total_investments || 0}
            subtitle="Portfolio companies"
            color="bg-orange-600"
          />
        </div>

        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* AMC Management */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <Building2 size={24} className="text-blue-600" />
              </div>
              <span className="text-2xl font-bold text-gray-900">{analytics?.total_amcs || 0}</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">AMC Management</h3>
            <p className="text-gray-600 text-sm mb-4">
              Create, manage, and monitor Asset Management Companies on your platform.
            </p>
            <button
              onClick={() => navigate('/owner/amcs')}
              className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Manage AMCs
              <ArrowRight size={18} className="ml-2" />
            </button>
          </div>

          {/* Platform Status */}
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl shadow-sm p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-lg bg-white bg-opacity-20 flex items-center justify-center">
                <Activity size={24} className="text-white" />
              </div>
              <span className="px-3 py-1 bg-green-500 text-white text-xs font-semibold rounded-full">
                Active
              </span>
            </div>
            <h3 className="text-lg font-semibold mb-2">Platform Status</h3>
            <p className="text-blue-100 text-sm mb-4">
              All systems operational. {analytics?.active_amcs || 0} AMCs are currently active.
            </p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-blue-200">Active AMCs</p>
                <p className="text-xl font-bold">{analytics?.active_amcs || 0}</p>
              </div>
              <div>
                <p className="text-blue-200">Inactive AMCs</p>
                <p className="text-xl font-bold">{analytics?.inactive_amcs || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/owner/amcs')}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-600 hover:bg-blue-50 transition-colors"
            >
              <div className="flex items-center">
                <Building2 size={20} className="text-blue-600 mr-3" />
                <span className="text-sm font-medium text-gray-700">View All AMCs</span>
              </div>
              <ArrowRight size={16} className="text-gray-400" />
            </button>
            <button
              onClick={() => navigate('/owner/amcs?action=create')}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-green-600 hover:bg-green-50 transition-colors"
            >
              <div className="flex items-center">
                <Building2 size={20} className="text-green-600 mr-3" />
                <span className="text-sm font-medium text-gray-700">Create New AMC</span>
              </div>
              <ArrowRight size={16} className="text-gray-400" />
            </button>
            <button
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-purple-600 hover:bg-purple-50 transition-colors"
            >
              <div className="flex items-center">
                <BarChart3 size={20} className="text-purple-600 mr-3" />
                <span className="text-sm font-medium text-gray-700">View Reports</span>
              </div>
              <ArrowRight size={16} className="text-gray-400" />
            </button>
            {canManageTeam && (
              <button
                onClick={() => navigate('/owner/users')}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-indigo-600 hover:bg-indigo-50 transition-colors"
                data-testid="go-to-owner-team"
              >
                <div className="flex items-center">
                  <UserCog size={20} className="text-indigo-600 mr-3" />
                  <span className="text-sm font-medium text-gray-700">Owner Team</span>
                </div>
                <ArrowRight size={16} className="text-gray-400" />
              </button>
            )}
            {user?.owner_capabilities?.is_super && (
              <button
                onClick={() => navigate('/owner/settings')}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-teal-600 hover:bg-teal-50 transition-colors"
                data-testid="go-to-email-settings"
              >
                <div className="flex items-center">
                  <Mail size={20} className="text-teal-600 mr-3" />
                  <span className="text-sm font-medium text-gray-700">Email Settings</span>
                </div>
                <ArrowRight size={16} className="text-gray-400" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerDashboard;
